import { useEffect, useState } from 'react'
import { SlidersHorizontal } from 'lucide-react'
import { HelpIcon, Button, Skeleton } from '../../core/index.js'
import { WorkerPreferences } from './WorkerPreferences.js'
import { ReleasedMonthHeader, ReleasedMonthNotes, type ReleasedMonthData } from '../components/WorkerScheduleResult.js'
import { bridgeShiftsOf, dayMarksOf } from '../components/scheduleDayMarks.js'
import { WorkerBoardCard } from '../components/WorkerBoardCard.js'
import { PastPreferencesCard, PastPreferencesMonthView } from '../components/PastPreferences.js'
import { useDraftSchedule, useShiftPreferences, useWorkerBoard, useWorkerBridgeCallouts, useWorkerScheduleResult } from '../hooks.js'
import { describePeriod } from '../components/preferencesText.js'
import { useAvailableShifts, useBridgeEnrollment, useClinicalBridge } from '../index.js'
import { acceptingPeriods, biddingDetail, biddingHeadline, biddingStatusOf, openBiddingPeriods, preferencesCloseLine } from '../components/biddingStatus.js'
import { useHolidayEvents } from '../../facilities/index.js'
import { useWorkerOutcomes } from '../../partner/index.js'
import { ApiError } from '../../core/index.js'
import type { ClinicalBridgeStatus, WorkerBoardView, WorkerScheduleResultView } from '../index.js'
import { ScheduleCalendar } from '../components/ScheduleCalendar.js'
import type { DayEnrollment } from '../components/ScheduleDayDetails.js'
import { bridgeTerms, canEnroll, enrolledSummary, enrollmentBlocker, isEnrollableDate, enrollmentLimits } from '../components/bridgeText.js'
import { draftedShifts, shiftMonth, todayDate, type ScheduledShift } from '../components/scheduleCalendarData.js'
import { EndpointHint } from '../../core/index.js'
import { TIER_LABELS } from '../../core/index.js'
import { LoadFailure } from '../../core/index.js'


function errorMessage(error: unknown): string | null {
  if (error instanceof ApiError) {
    return error.message
  }
  return error instanceof Error ? 'That could not be saved. Try again in a moment.' : null
}

/** The Clinical Bridge panel under the calendar: the facility's terms, the worker's standing, and what they can do. */
function BridgePanel({ bridge, today }: { bridge: ClinicalBridgeStatus; today: string }) {
  const blocker = enrollmentBlocker(bridge)
  return (
    <div className="mt-3 pt-3 border-t border-marine/15 text-[10px] text-ink-mid leading-relaxed">
      <div className="flex items-baseline gap-2">
        <strong className="text-ink">Clinical Bridge</strong>
        <HelpIcon topic="schedule.worker-bridge" />
        <EndpointHint>GET /workers/{'{workerId}'}/clinical-bridge</EndpointHint>
      </div>
      {blocker ? (
        <p className="mt-1">{blocker}</p>
      ) : (
        <>
          <p className="mt-1 text-ink">{enrolledSummary(bridge, today)}</p>
          <ul className="mt-1.5 flex flex-col gap-0.5">
            {bridgeTerms(bridge.rules).map((line) => (
              <li key={line}>{line}</li>
            ))}
            {enrollmentLimits(bridge.rules) && <li>{enrollmentLimits(bridge.rules)}</li>}
          </ul>
        </>
      )}
    </div>
  )
}

/**
 * The worker's Shifts tab: what I have done and what is coming, together.
 * One calendar holding the shifts they have had, the facility's open
 * shifts, their Clinical Bridge days and any holiday event - a date opens
 * its details, and a worker the facility has made eligible enrolls for a
 * day or withdraws from it there. Bidding on open shifts itself belongs to
 * the scheduler (docs/plans/scheduling-system-plan.md). It replaced the
 * separate Schedule tab, which listed the same as cards.
 */
export function WorkerShifts({ workerId, facilityId, openPreferencesNonce = 0 }: { workerId: string; facilityId: string; /** Bumped by the Home banner to open the preferences wizard. */ openPreferencesNonce?: number }) {
  const [view, setView] = useState<'calendar' | 'preferences' | 'past'>(openPreferencesNonce > 0 ? 'preferences' : 'calendar')
  // The past month being read back, while `view` is 'past'.
  const [pastPeriod, setPastPeriod] = useState<string | null>(null)
  useEffect(() => {
    if (openPreferencesNonce > 0) {
      setView('preferences')
    }
  }, [openPreferencesNonce])
  const preferencesQuery = useShiftPreferences(workerId)
  const draftQuery = useDraftSchedule(workerId)
  const outcomesQuery = useWorkerOutcomes(workerId)
  const shiftsQuery = useAvailableShifts(facilityId)
  const bridgeQuery = useClinicalBridge(workerId)
  const holidaysQuery = useHolidayEvents(facilityId)
  // The released schedule of this month and the two after it, as each month's board holds it once released (swaps and pickups applied),
  // else the run's result. The third: with the preferences close enforced, the month being bid for is often two months out, and a
  // facility may build and release it early (the demo does).
  const thisMonth = todayDate().slice(0, 7)
  const nextMonth = shiftMonth(thisMonth, 1)
  const monthAfter = shiftMonth(thisMonth, 2)
  const resultThisMonth = useWorkerScheduleResult(workerId, thisMonth)
  const resultNextMonth = useWorkerScheduleResult(workerId, nextMonth)
  const resultMonthAfter = useWorkerScheduleResult(workerId, monthAfter)
  const boardThisMonth = useWorkerBoard(workerId, thisMonth)
  const boardNextMonth = useWorkerBoard(workerId, nextMonth)
  const boardMonthAfter = useWorkerBoard(workerId, monthAfter)
  // A month before this one the calendar has stepped back to (demo history plan H0): its released schedule, as the board left it.
  // Not past, it asks for this month again, which is already read, so nothing more is fetched.
  const [calendarMonth, setCalendarMonth] = useState(thisMonth)
  const pastMonth = calendarMonth < thisMonth ? calendarMonth : thisMonth
  const resultPastMonth = useWorkerScheduleResult(workerId, pastMonth)
  const boardPastMonth = useWorkerBoard(workerId, pastMonth)
  // Each released month the calendar can show, with its board once read (worker calendar design, 26 October 2026).
  const bridgeCalloutsQuery = useWorkerBridgeCallouts(workerId)
  const monthReads: readonly { period: string; result: WorkerScheduleResultView | undefined; board: WorkerBoardView | undefined }[] = [
    ...(pastMonth === thisMonth ? [] : [{ period: pastMonth, result: resultPastMonth.data, board: boardPastMonth.data }]),
    { period: thisMonth, result: resultThisMonth.data, board: boardThisMonth.data },
    { period: nextMonth, result: resultNextMonth.data, board: boardNextMonth.data },
    { period: monthAfter, result: resultMonthAfter.data, board: boardMonthAfter.data },
  ]
  const released = new Map<string, ReleasedMonthData>(
    monthReads.flatMap((read) => (read.result?.result ? [[read.period, { period: read.period, result: read.result.result, releasedAt: read.result.releasedAt, board: read.board ?? null }] as const] : [])),
  )
  const bridgeCallouts = bridgeCalloutsQuery.data ?? []
  const fromSchedule: ScheduledShift[] = monthReads.flatMap((read) => read.board?.assignments ?? read.result?.result?.awarded ?? [])
  // A Clinical Bridge shift the worker took is theirs though no schedule holds it.
  const scheduled: ScheduledShift[] = [...fromSchedule, ...bridgeShiftsOf(bridgeCallouts).filter((shift) => !fromSchedule.some((entry) => entry.date === shift.date))]
  const marks = [...released.values()].flatMap((month) => dayMarksOf({ result: month.result, board: month.board, bridgeCallouts: bridgeCallouts.filter((callout) => callout.shift.date.startsWith(`${month.period}-`)) }))
  const unitNames = new Map(monthReads.flatMap((read) => Object.entries(read.board?.unitNames ?? {})))
  const { enroll, withdraw } = useBridgeEnrollment(workerId)
  const [lastError, setLastError] = useState<string | null>(null)

  const others = [outcomesQuery, shiftsQuery, bridgeQuery, holidaysQuery]
  if (others.some((query) => query.isPending)) {
    return <Skeleton lines={5} className="p-8" />
  }
  if (others.some((query) => query.isError) || !outcomesQuery.data || !shiftsQuery.data || !bridgeQuery.data || !holidaysQuery.data) {
    return <LoadFailure className="p-8" message="Your shifts could not be loaded." onRetry={() => void Promise.all(others.map((query) => query.refetch()))} retrying={others.some((query) => query.isFetching)} />
  }

  // The months bidding is open for: those taking preferences that the facility has published shifts for from today on, next month first.
  const today = todayDate()
  const openPeriods = preferencesQuery.data ? openBiddingPeriods(preferencesQuery.data, shiftsQuery.data, today) : []

  // The wizard opens only for a month with something to bid on; the Home banner's shortcut lands on the calendar otherwise.
  if (view === 'preferences' && openPeriods.length > 0) {
    return <WorkerPreferences workerId={workerId} periods={openPeriods} onDone={() => setView('calendar')} />
  }
  if (view === 'past' && pastPeriod) {
    return <PastPreferencesMonthView workerId={workerId} period={pastPeriod} onDone={() => setView('calendar')} />
  }

  const outcomes = outcomesQuery.data
  const bridge = bridgeQuery.data
  const preferences = preferencesQuery.data
  const draft = draftQuery.data?.draft ?? null
  // The month being bid for: the earliest still taking preferences.
  const biddingMonth = preferences ? (acceptingPeriods(preferences)[0] ?? preferences.periodsOpen.next) : nextMonth
  // Until the worker submits, the date their preferences close is the thing to know.
  const closeLine = preferences && openPeriods.length > 0 && draft?.status !== 'accepted' ? preferencesCloseLine(preferences) : null
  const draftLine = draft ? (draft.status === 'accepted' ? `Schedule submitted · ${draft.hours} hrs` : `Draft built · ${draft.hours} hrs · not yet submitted`) : null
  // Where bidding stands, by the facility's preferences window - the per-tier bid window it replaced was never computed.
  const bidding = preferences ? biddingStatusOf(preferences, shiftsQuery.data, today, draft) : null
  const assigned = bidding?.kind === 'assigned'
  // The draft's shifts beside the open shifts until its month is released, and the calendar opens on that month.
  const drafted = draftedShifts(draft, scheduled)

  async function act(mutation: typeof enroll, date: string): Promise<void> {
    setLastError(null)
    try {
      await mutation.mutateAsync(date)
    } catch (error) {
      setLastError(errorMessage(error))
    }
  }

  const enrollment: DayEnrollment | null = canEnroll(bridge)
    ? {
        enrollable: (date) => isEnrollableDate(date, today),
        onEnroll: (date) => void act(enroll, date),
        onWithdraw: (date) => void act(withdraw, date),
        pending: enroll.isPending || withdraw.isPending,
        error: lastError,
        scoreBoostPerShift: bridge.rules.scoreBoostPerShift,
      }
    : null

  return (
    <div className="max-w-md mx-auto flex flex-col gap-4 p-4">
      {/* The demo's "Bid now": what the worker asks of a month, saved here, built by the scheduler - offered only once the facility has published that month's shifts. */}
      {preferencesQuery.isPending ? null : openPeriods.length === 0 && !draft ? (
        <div className="border border-marine/15 bg-white p-3">
          <div className="text-[9px] font-bold uppercase tracking-wide text-ink-mid">{preferences ? describePeriod(biddingMonth) : 'Next month'}</div>
          <div className="text-xs font-extrabold text-ink">{bidding?.kind === 'upcoming' ? biddingHeadline(bidding) : assigned ? 'Your facility assigns your schedule' : 'Bidding opens when the schedule is published'}</div>
          <div className="text-[10px] text-ink-mid">
            {bidding?.kind === 'upcoming'
              ? biddingDetail(bidding)
              : assigned
              ? `It can be built once your facility publishes ${preferences ? describePeriod(biddingMonth) : 'next month'}.`
              : `Your facility has not published shifts for ${preferences ? describePeriod(biddingMonth) : 'next month'} yet. You can tell your scheduler what you want once it does.`}
          </div>
        </div>
      ) : assigned ? (
      <div className={`flex items-center justify-between gap-3 border p-3 ${draft ? 'border-marine/15 bg-white' : 'border-coral/50 bg-coral-lt'}`}>
        <div className="min-w-0">
          <div className="text-[9px] font-bold uppercase tracking-wide text-ink-mid">{describePeriod(openPeriods[0] ?? preferences?.period ?? '')}</div>
          <div className="text-xs font-extrabold text-ink">{draftLine ?? 'Your facility assigns your schedule'}</div>
          <div className="text-[10px] text-ink-mid">{draft?.status === 'accepted' ? 'Your scheduler builds the month from it.' : bidding ? biddingDetail(bidding) : ''}</div>
        </div>
        {openPeriods.length > 0 && (
          <Button icon={SlidersHorizontal} size="sm" variant={draft ? 'secondary' : 'primary'} onClick={() => setView('preferences')}>
            {draft ? 'Open' : 'Build'}
          </Button>
        )}
      </div>
      ) : (
      <div className={`flex items-center justify-between gap-3 border p-3 ${preferences?.preferences ? 'border-marine/15 bg-white' : 'border-coral/50 bg-coral-lt'}`}>
        <div className="min-w-0">
          <div className="text-[9px] font-bold uppercase tracking-wide text-ink-mid">{describePeriod(openPeriods[0] ?? preferences?.period ?? '')}</div>
          <div className="text-xs font-extrabold text-ink">{draftLine ?? (preferences?.preferences ? 'Preferences saved, no draft yet' : 'Tell your scheduler what you want')}</div>
          <div className="text-[10px] text-ink-mid">{draft?.status === 'accepted' ? 'Your scheduler builds the month from it. Change it any time until then.' : preferences?.preferences ? 'Build your schedule from them and submit it.' : 'Shift order, hours, PTO and days off.'}</div>
          {closeLine && <div className="text-[10px] font-bold text-coral">{closeLine}</div>}
        </div>
        {openPeriods.length > 0 && (
          <Button icon={SlidersHorizontal} size="sm" variant={preferences?.preferences ? 'secondary' : 'primary'} onClick={() => setView('preferences')}>
            {draft ? 'Open' : preferences?.preferences ? 'Build' : 'Bid now'}
          </Button>
        )}
      </div>
      )}
      <div>
        <div className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide text-ink-mid mb-1.5">Schedule<HelpIcon topic="schedule.worker-shifts" /></div>
        <div className="bg-white border border-marine/15 p-4">
          {bidding && <div className="text-xs font-extrabold text-ink mb-0.5">{biddingHeadline(bidding)}</div>}
          <div className="text-[11px] text-ink-mid mb-3">
            {bidding ? `${TIER_LABELS[bidding.tier]} tier · ` : ''}tap a date for its details
          </div>

          {/* A released month is the calendar's: its counts head it and what changed since the release follows it (worker calendar design, 26 October 2026). */}
          <ScheduleCalendar
            records={{ outcomes, available: shiftsQuery.data, bridgeDates: bridge.preBidDates, holidays: holidaysQuery.data, scheduled, drafted, marks, unitNames }}
            enrollment={enrollment}
            initialMonth={drafted.length > 0 && draft ? draft.period : undefined}
            onMonthChange={setCalendarMonth}
            header={(month) => {
              const data = released.get(month)
              return data ? <ReleasedMonthHeader data={data} /> : null
            }}
            footer={(month) => {
              const data = released.get(month)
              return data ? <ReleasedMonthNotes data={data} unitNames={unitNames} /> : null
            }}
          />

          <BridgePanel bridge={bridge} today={today} />
        </div>
      </div>
      {/* After the release: swaps, the change window, open shifts offered to the worker (plan phase C), for each month released. */}
      <WorkerBoardCard workerId={workerId} period={thisMonth} />
      <WorkerBoardCard workerId={workerId} period={nextMonth} />
      <WorkerBoardCard workerId={workerId} period={monthAfter} />
      {/* What the worker asked for in months that can no longer change - released, or closed - read-only. */}
      <PastPreferencesCard
        months={preferences?.past ?? []}
        onOpen={(period) => {
          setPastPeriod(period)
          setView('past')
        }}
      />
    </div>
  )
}
